'use strict';


import React, {Component, ErrorInfo, ReactNode} from 'react';
import parse from 'html-react-parser';

/**
 * Definition of components props
 * @memberof ErrorBoundary
 */
type Props = {
    children? : ReactNode
}

type State = {
    hasError : boolean;
    error? : Error;
}

/**
 * ErrorBoundary to catch JS errors while rendering components.
 * Prevents white screens if error occurs in wrapped components.
 * See https://reactjs.org/docs/error-boundaries.html
 */
class ErrorBoundary extends Component<Props, State> {


    /**
     * Component constructor
     * @param props
     */
    constructor( props : Props ) {
        super( props );
        this.state = { hasError: false };
    }

    /**
     * Handle error of wrapped component
     * @param error
     * @return {{hasError: boolean}}
     */
    static getDerivedStateFromError( error : Error ) {
        // Update state so the next render will show the fallback UI.
        console.error( error );
        return { hasError: true, error };
    }

    /**
     * Error handler. Allows further handling as error logging
     * @param error
     * @param errorInfo
     */
    componentDidCatch( error : Error, errorInfo : ErrorInfo ) {
        // You can also log the error to an error reporting service
        //logErrorToMyService( error, errorInfo );
    }

    /**
     * React render method
     * @return {JSX.Element|React.ReactNode}
     */
    render() {
        const stack = this.state.error?.stack?.split( '\n' ) ?? [];

        stack.shift();

        if ( this.state.hasError ) {
            // You can render any custom fallback UI
            return <div className="yn-content-panel-container">
                <h1 className="yn-error">Something went wrong</h1>
                {parse( this.state.error?.toHTML() ?? '' )}
                <br/>
                <div className="yn-error-subtitle">Stack</div>
                <div className="yn-error-message">
                    {parse( stack?.join( '<br/>' ) )}
                </div>
            </div>;
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
